<script setup lang="ts">
import { computed, useTemplateRef } from "vue";
import { UiFormBuilder, UiButton } from "@shopexperts/shared";
import type { BuilderRowConfig } from "@shopexperts/shared";

import {
  expertLevelFormSchema,
  type ExpertLevelFormValues,
} from "@/app/composables/expert-levels/expertLevelForm";

const props = defineProps<{
  submitLabel?: string;
  isEditing?: boolean;
  isSubmitting?: boolean;
}>();

const emit = defineEmits<{
  (e: "submit", value: ExpertLevelFormValues): void;
  (e: "cancel"): void;
}>();

const model = defineModel<ExpertLevelFormValues>({ required: true });

const rows = computed<BuilderRowConfig[]>(() => [
  {
    columns: [
      {
        heading: "Public",
        subtitle: "How this level appears to experts in the wallet.",
        divider: false,
      },
    ],
  },
  {
    columns: [
      {
        name: "public_title",
        type: "text",
        label: "Title",
        hint: "Shown to experts. Example: Bronze, Silver, Gold.",
        props: { placeholder: "e.g., Silver" },
      },
    ],
  },
  {
    columns: [
      {
        name: "public_description",
        type: "textarea",
        label: "Description",
        hint: "Short tagline displayed alongside the level. Optional.",
        props: { placeholder: "What this level means for the expert.", rows: 3 },
      },
    ],
  },
  {
    columns: [
      {
        name: "commission_percent",
        type: "number",
        label: "Commission rate",
        inlineHint: "%",
        hint: "Percent of expert revenue kept as commission. Stored as basis points.",
        props: { min: 0, max: 100, step: 0.25, placeholder: "0" },
      },
      {
        name: "threshold_dollars",
        type: "currency",
        label: "Auto-enroll threshold",
        hint: "Lifetime gross sales that auto-promote experts to this level.",
        props: { min: 0, step: 1 },
      },
    ],
  },
  {
    columns: [
      {
        heading: "Admin",
        subtitle: "Internal controls. Not visible to experts.",
        divider: true,
      },
    ],
  },
  {
    columns: [
      {
        name: "sort_order",
        type: "number",
        label: "Sort order",
        hint: "Lower numbers appear first. Use this to order levels from entry to elite.",
        props: { min: 0, step: 1, placeholder: "0" },
      },
      {
        name: "is_active",
        type: "checkbox",
        label: "Status",
        hint: "Inactive levels stay on existing experts but stop auto-enrollment.",
        props: { label: "Level is active and eligible for auto-enrollment" },
      },
    ],
  },
  {
    columns: [
      {
        name: "internal_notes",
        type: "textarea",
        label: "Internal notes",
        hint: "Admin-only context. Optional.",
        props: { placeholder: "Notes for the admin team.", rows: 4 },
      },
    ],
  },
]);

const formRef = useTemplateRef("formRef");

defineExpose({
  resetForm: () => formRef.value?.resetForm(),
  validate: (scrollToFirstError?: boolean) => formRef.value?.validate(scrollToFirstError),
});

function onSubmit(values: ExpertLevelFormValues) {
  emit("submit", values);
}

function onCancel() {
  emit("cancel");
}
</script>

<template>
  <UiFormBuilder
    ref="formRef"
    v-model="model"
    :schema="expertLevelFormSchema"
    :rows="rows"
    scroll-to-first-error
    @submit="onSubmit"
  >
    <template #actions>
      <div class="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-2">
        <UiButton variant="outline" label="Cancel" :disabled="props.isSubmitting" @click="onCancel" />
        <UiButton
          type="submit"
          variant="primary"
          :label="props.submitLabel || (props.isEditing ? 'Save Changes' : 'Create Level')"
          :disabled="props.isSubmitting"
        />
      </div>
    </template>
  </UiFormBuilder>
</template>
